/**
 * shared/narration/rates.ts — ONE home for the speech-rate constants.
 *
 * Four design documents carried four values for the same physical constant (11.5 / 14 / 15.3 / 16.0
 * chars per second of speech), and the co-read sync axis used the stale books number — a systematic
 * ~30% overestimate of every un-narrated stretch of every book. Both the server's ETA and the
 * client's "warming up — about 20 seconds" copy import from here. Two constants for one number is how
 * UI copy goes quietly wrong.
 *
 * CI greps client/ for bare `/ 11`, `/ 14` and `* 56`.
 */

/** Measured 2026-08-06, spark-1 (100.127.247.67:8095), voice=karti, variant=turbo. */
export const SPEECH_CHARS_PER_SEC = 15.4; // plain prose; 1216 chars -> 78.98 s
export const SPEECH_CHARS_PER_SEC_DENSE = 8.0; // number/abbreviation-dense
export const GPU_CHARS_PER_SEC = 56; // wall clock; 1216 chars -> 21.50 s = 3.67x realtime

/**
 * How long this text will take to SAY. Used for `sentences.durMs` where `durEstimated = 1`, for the
 * "~14 min left in chapter" footer, and for the un-narrated stretches of the linear axis (§4.6).
 *
 * `dense` is for number- and abbreviation-heavy text — a verse reference or a table of figures takes
 * nearly twice as long per character as prose once the normalizer has expanded it.
 */
export const estimateSpeechMs = (chars: number, dense = false): number =>
  (chars / (dense ? SPEECH_CHARS_PER_SEC_DENSE : SPEECH_CHARS_PER_SEC)) * 1000;

/**
 * How long the GPU will take to PRODUCE this text. This is the number behind every ETA the user sees,
 * including the stall state. It is wall clock on a warm model — a cold start is not covered by it and
 * is not an outage: the generate timeout is 180 seconds, and only the health probe is impatient.
 */
export const estimateGpuMs = (chars: number): number => (chars / GPU_CHARS_PER_SEC) * 1000;
